import { useState } from 'react';
import { AlignJustifyIcon, Search } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { ChangeLanguageButton } from '@/components/change-language-button';
import { LanguageSelector } from '@/components/language-selector';
import { SocialButtons } from '@/components/social-buttons';
import { Link } from '@/components/ui/link';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { Language } from '@/types/language';

import { SendRecipeButton } from './send-recipe-button';

interface MobileNavProps {
    menuItems: Array<{ title: string; href: string }>;
    isActivePath: (href: string) => boolean;
    hasMultipleLanguages?: boolean;
    languages?: Language[];
}

export const MobileNav = ({ menuItems, isActivePath, hasMultipleLanguages = false, languages }: MobileNavProps) => {
    const t = useTranslations();
    const [isOpen, setIsOpen] = useState(false);
    const onClose = () => {
        setIsOpen(false);
    };

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
                <div className='cursor-pointer lg:hidden'>
                    <AlignJustifyIcon className='size-6 w-full text-white' />
                    <span className='sr-only'>{t('toggle-menu')}</span>
                </div>
            </SheetTrigger>
            <SheetContent
                side='right'
                className='bg-purple-light flex min-h-dvh w-[90%] flex-col px-0 pb-[env(safe-area-inset-bottom)] opacity-80'
                onPointerDownOutside={e => e.preventDefault()}
                onInteractOutside={e => e.preventDefault()}
            >
                <SheetTitle className='sr-only'>{t('navigation')}</SheetTitle>
                <SheetHeader className='bg-white p-6'>
                    <div onClick={onClose}>
                        <Link href='/' className='flex items-center'>
                            <Image src='/svg/logo.svg' alt='Logo' width={110} height={17.5} priority loading='eager' />
                        </Link>
                    </div>
                </SheetHeader>
                <div className='flex flex-1 flex-col justify-between'>
                    <div className='flex items-center justify-center gap-4 px-4 py-6 '>
                        <div className='flex-1' onClick={onClose}>
                            <Link
                                href='/search'
                                className='flex h-10 cursor-pointer items-center rounded-none bg-white bg-opacity-[0.1] px-3 text-white'
                            >
                                <span className='flex-1 '>{t('search')}</span>
                                <Search strokeWidth={2} color='white' size={24} />
                            </Link>
                        </div>
                        {hasMultipleLanguages ? (
                            <div className=' bg-white bg-opacity-[0.1] p-2'>
                                <LanguageSelector languages={languages || []} />
                            </div>
                        ) : (
                            languages &&
                            languages.length > 1 && (
                                <div className=' bg-white bg-opacity-[0.1] p-2'>
                                    <ChangeLanguageButton languages={languages} />
                                </div>
                            )
                        )}
                    </div>

                    <div className='mt-auto flex flex-col items-center justify-center gap-6 px-4'>
                        {menuItems.map(item => {
                            const isActive = isActivePath(item.href);
                            return (
                                <div key={item.href} onClick={onClose}>
                                    <Link
                                        href={item.href}
                                        className={cn(
                                            'text-lg uppercase tracking-wide text-white/80 transition-colors hover:text-white',
                                            {
                                                'border-b-[3px] border-yellow font-medium text-white': isActive,
                                            },
                                        )}
                                    >
                                        {t(item.title)}
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                    <div onClick={onClose} className='mb-safe mt-auto flex w-full flex-col justify-center gap-4 p-4'>
                        <SendRecipeButton isActivePath={isActivePath} />
                        <SocialButtons isMenu />
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};
